/**
 * Notification grouping (Phase C) — collapses a run of same-kind
 * notifications into one summary row so the bell doesn't become an
 * unscannable wall when a coach publishes a batch of feedback. A
 * group of 3+ notifications sharing the same `(source.type, link)`
 * renders as a single "3 new feedback items to review" row that opens
 * the shared destination and marks/dismisses the whole group at once.
 *
 * Pure function (no React) so it's unit-testable and the dropdown just
 * maps over the result.
 */

import type { AppNotification } from "@/lib/types";

/** A row in the (possibly grouped) bell list. A singleton wraps one
 * notification; a group carries the shared destination + the keys it
 * covers so open/dismiss can act on all of them. */
export type NotificationRow =
  | { kind: "single"; notification: AppNotification }
  | {
      kind: "group";
      /** Stable id for React + testing: `${type}:${link}`. */
      id: string;
      title: string;
      link: string;
      count: number;
      /** Unread if ANY member is unread. */
      isRead: boolean;
      /** Every member key — for batch markRead / dismiss. */
      keys: string[];
      /** Most-recent member's created_at, for sort stability. */
      created_at: string;
    };

/** Collapse groups of 3+ notifications that share `(source.type,
 * link)` into one row; smaller runs stay as individual rows. Input
 * order is preserved (the backend already sorts newest-first), and a
 * group takes the position of its first member. */
/** The part of a link that identifies the DESTINATION rather than the
 * item.
 *
 * Phase 2.2 gave notifications per-item deep-links (`?tab=review&note=42`)
 * so the bell lands on the thing that notified you. Bucketing on the raw
 * link would then put every item in its own bucket and silently kill the
 * threshold collapse — "3 new feedback items to review" would never fire
 * again, because no two links would ever match.
 *
 * Grouping is about "these all go to the same place", so drop the
 * item-identifying params and keep the rest. A malformed link is
 * returned unchanged: grouping is cosmetic, and a bad value should
 * degrade to "does not group" rather than throw.
 */
const ITEM_PARAMS = ["note", "clip", "goal", "summary", "library_item_id"];

export function groupingLink(link: string): string {
  const qs = link.indexOf("?");
  if (qs === -1) return link;
  try {
    const params = new URLSearchParams(link.slice(qs + 1));
    for (const key of ITEM_PARAMS) params.delete(key);
    const rest = params.toString();
    return rest ? `${link.slice(0, qs)}?${rest}` : link.slice(0, qs);
  } catch {
    return link;
  }
}

export function groupNotifications(
  notifications: AppNotification[],
  /** Minimum run length to collapse. Default 3 — pairs stay expanded
   * so the coach still sees both. */
  threshold = 3,
): NotificationRow[] {
  // Bucket by (type, link), preserving first-seen order.
  const order: string[] = [];
  const buckets = new Map<string, AppNotification[]>();
  for (const n of notifications) {
    const type = String(n.source?.type ?? "");
    const id = `${type}:${groupingLink(n.link)}`;
    if (!buckets.has(id)) {
      buckets.set(id, []);
      order.push(id);
    }
    buckets.get(id)!.push(n);
  }

  const rows: NotificationRow[] = [];
  for (const id of order) {
    const members = buckets.get(id)!;
    if (members.length < threshold) {
      for (const n of members) rows.push({ kind: "single", notification: n });
      continue;
    }
    rows.push({
      kind: "group",
      id,
      title: groupTitle(members),
      link: members[0]!.link,
      count: members.length,
      isRead: members.every((n) => n.is_read),
      keys: members.map((n) => n.key),
      created_at: members[0]!.created_at,
    });
  }
  return rows;
}

/** Group copy. Notifications carry no coach-private text in their
 * title/body (the builder enforces that), and a group label is even
 * leaner — just a count + the shared action. */
function groupTitle(members: AppNotification[]): string {
  const n = members.length;
  const type = String(members[0]?.source?.type ?? "");
  if (type === "note" || type === "clip") {
    return `${n} new feedback items to review`;
  }
  if (type === "match_summary") {
    return `${n} new match recaps`;
  }
  if (type === "goal") {
    return `${n} goals need a response`;
  }
  return `${n} new notifications`;
}
